import firebase from 'firebase/app';
import 'firebase/storage';

// PDF upload size limit: 1MB
const MAX_PDF_SIZE = 1 * 1024 * 1024; // 1MB in bytes

class PdfUploadService {
  /**
   * Upload PDF file to Firebase Storage following the same pattern as video recordings
   * @param {File} pdfFile - The PDF file to upload
   * @param {string} examCode - Exam identifier
   * @param {string} questionId - Question identifier
   * @param {Function} onProgress - Progress callback function
   * @returns {Promise<Object>} - Returns minimal answer object like video recordings
   */
  static async uploadPdf(pdfFile, examCode, questionId, onProgress = null) {
    return new Promise((resolve, reject) => {
      try {
        // Validate file size before upload (1MB limit)
        if (pdfFile.size > MAX_PDF_SIZE) {
          const fileSizeMB = (pdfFile.size / (1024 * 1024)).toFixed(2);
          reject(new Error(`PDF size exceeds 1MB limit. Your file is ${fileSizeMB}MB.`));
          return;
        }
        
        // Generate unique filename like video recordings
        const timestamp = Date.now();
        const randomId = Math.random().toString(36).substring(2, 15);
        const fileName = `pdf_${timestamp}_${randomId}.pdf`;
        
        // Debug logging (same as video recordings)
        console.log('PDF upload details:', {
          examCode,
          questionId,
          fileName,
          fileSize: pdfFile.size,
          maxSize: MAX_PDF_SIZE,
          storagePath: `submissions/${examCode}/${questionId}/${fileName}`
        });
        
        // Same storage path structure as video recordings: submissions/examCode/questionId/filename
        const storageRef = firebase.storage().ref(`submissions/${examCode}/${questionId}/${fileName}`);
        
        // Upload with progress tracking (same as video recordings)
        const uploadTask = storageRef.put(pdfFile);
        
        uploadTask.on('state_changed',
          (snapshot) => {
            if (onProgress) {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              onProgress(Math.round(progress));
            }
          },
          (error) => {
            console.error('PDF upload failed:', error);
            reject(error);
          },
          async () => {
            try {
              // Ultra-minimal storage: only store the path (exact same as video recordings)
              const storagePath = `${examCode}/${questionId}/${fileName}`;
              
              const pdfAnswer = {
                pdfPath: storagePath // Only the relative path (without 'submissions/' prefix)
                // No metadata - everything else can be derived from the file when needed
              };
              
              console.log('Final PDF answer object (ultra-minimal):', pdfAnswer);
              resolve(pdfAnswer);
            } catch (error) {
              console.error('Error getting PDF download URL:', error);
              reject(error);
            }
          }
        );
      } catch (error) {
        console.error('PDF upload error:', error);
        reject(error);
      }
    });
  }

  /**
   * Get PDF download URL from storage path (same pattern as video recordings)
   * @param {string} pdfPath - The storage path
   * @returns {Promise<string>} - Download URL
   */
  static async getPdfUrl(pdfPath) {
    try {
      const storageRef = firebase.storage().ref(`submissions/${pdfPath}`);
      return await storageRef.getDownloadURL();
    } catch (error) {
      console.error('Error getting PDF URL:', error);
      throw error;
    }
  }

  /**
   * Check if answer has PDF (same pattern as video recordings)
   * @param {Object} answer - Answer object
   * @returns {boolean} - True if has PDF
   */
  static hasPdf(answer) {
    return answer && answer.pdfPath;
  }

  /**
   * Get PDF file info from answer (same pattern as video recordings)
   * @param {Object} answer - Answer object
   * @returns {Object|null} - PDF info or null
   */
  static getPdfInfo(answer) {
    if (!this.hasPdf(answer)) return null;
    
    return {
      path: answer.pdfPath,
      fileName: answer.pdfPath.split('/').pop(),
      // Other metadata can be derived when needed
    };
  }

  /**
   * Get maximum allowed PDF size
   * @returns {number} Maximum size in bytes (1MB)
   */
  static getMaxSize() {
    return MAX_PDF_SIZE;
  }

  /**
   * Validate PDF file size
   * @param {File} file - PDF file to validate
   * @returns {Object} Validation result with isValid and error message
   */
  static validateFileSize(file) {
    if (!file) {
      return { isValid: false, error: 'No file provided' };
    }
    
    if (file.size > MAX_PDF_SIZE) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      return {
        isValid: false,
        error: `PDF size should be less than 1MB. Your file is ${fileSizeMB}MB.`
      };
    }
    
    return { isValid: true, error: null };
  }
}

export default PdfUploadService;
